/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */

import { Copy } from 'lucide-react';
import { Link } from 'react-router';

import TimelineElement from '../atoms/TimelineElement';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { toast } from '../ui/use-toast';

import { Modal } from '@/pages/projects/Modal';

export type AddToCiModalProps = {
    projectSlug: string;
    suiteSlug: string;
    isCiModalOpen: boolean;
    setIsCiModalOpen: (open: boolean) => void;
};

const AddToCiModal = ({ projectSlug, suiteSlug, isCiModalOpen, setIsCiModalOpen }: AddToCiModalProps) => {
    // eslint-disable-next-line max-len
    const API_URL = `https://api${window?.location?.href?.includes('app-staging.heal.dev') ? '-staging' : ''}.heal.dev/api`;

    let CI_SNIPPET = `name: Heal.dev CI
on:
  push: # on push to current branch
  schedule: # every day at 8
    - cron: '0 8 * * *'
  workflow_dispatch: # manual trigger
jobs:
  heal-dev:
    name: Heal.dev
    runs-on: ubuntu-latest
    steps:
      - name: Trigger Heal Suite Execution
        uses: heal-dev/trigger@v1
        with:
          api-token: \${{ secrets.HEAL_API_TOKEN }} # Required: Your Heal API token.
          suite: "${projectSlug}/${suiteSlug}"
          wait-for-results: "yes" # Optional: Wait for results
          comment-on-pr: "yes" # Optional: comment test results on PRs`;
    if (window?.location?.href?.includes('app-staging.heal.dev')) {
        const DOMAIN_LINE = `
          domain: "https://api-staging.heal.dev"`;
        CI_SNIPPET += DOMAIN_LINE;
    }

    // eslint-disable-next-line max-len
    const NODE_SNIPPET = `await fetch("${API_URL}/projects/${projectSlug}/suites/${suiteSlug}/trigger", {
  method: "POST",
  headers: {
      "Authorization": \`Bearer \${HEAL_API_TOKEN}\`,
      "Content-Type": "application/json"
    },
  body: JSON.stringify({}),
});`;

    // eslint-disable-next-line max-len
    const CURL_SNIPPET = `curl -X POST "${API_URL}/projects/${projectSlug}/suites/${suiteSlug}/trigger" \\
-H "Authorization: Bearer {{your-api-key}}" \\
-H "Content-Type: application/json" \\
-d '{}'`;

    return (
        <Modal isOpen={isCiModalOpen} onClose={() => setIsCiModalOpen(false)} title={''}>
            <div className="sm:w-[800px] sm:h-[600px] max-w-full">
                <Tabs defaultValue="github" className="relative w-full">
                    <div className="flex items-center justify-between pb-4">
                        <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0">
                            <TabsTrigger value="github" className="text-lg shadow-none">
                                GitHub
                            </TabsTrigger>
                            <TabsTrigger value="node" className="text-lg shadow-none">
                                node.js
                            </TabsTrigger>
                            <TabsTrigger value="bash" className="text-lg">
                                bash
                            </TabsTrigger>
                        </TabsList>
                    </div>
                    <TabsContent value="github">
                        <div>
                            <TimelineElement title={'Set your Heal API token'}>
                                <div>
                                    Create a{' '}
                                    <Link to={`/organisation/keys`} target="_blank" className="underline">
                                        Heal API key
                                    </Link>
                                    , and add it to your GitHub secrets as <code>HEAL_API_TOKEN</code>
                                </div>
                            </TimelineElement>
                            <TimelineElement
                                title={
                                    <div className="flex flex-row items-center gap-2">
                                        Add <code>heal-tests.yml</code> to your GitHub workflow.{' '}
                                        <button
                                            title="Copy GitHub Action snippet to clipboard"
                                            onClick={() => {
                                                toast({
                                                    title: 'Copied to clipboard',
                                                    duration: 5000,
                                                });
                                                navigator.clipboard.writeText(CI_SNIPPET);
                                            }}
                                            className="ms-auto px-2 py-1 hover:bg-purple-100 rounded"
                                        >
                                            <Copy size={14} color="rgb(168 85 247)" />
                                        </button>
                                    </div>
                                }
                            >
                                <p>Keep one or more of the triggers to run on PRs, schedule, or manually.</p>
                                <pre className="px-2 py-3 border rounded-lg bg-gray-100 text-sm">
                                    <code>{CI_SNIPPET}</code>
                                </pre>
                            </TimelineElement>
                            <TimelineElement
                                title={'(Optional) Pass variables, change the entrypoint, ...'}
                                hideTimeline
                            >
                                <div>
                                    Check out the{' '}
                                    <Link
                                        className="underline"
                                        target="_blank"
                                        to="https://github.com/heal-dev/trigger?tab=readme-ov-file#trigger"
                                    >
                                        docs
                                    </Link>
                                </div>
                            </TimelineElement>
                        </div>
                    </TabsContent>
                    <TabsContent value="node">
                        <div>
                            <TimelineElement title={'Set your Heal API token'}>
                                <div>
                                    Create a{' '}
                                    <Link to={`/organisation/keys`} target="_blank" className="underline">
                                        Heal API key
                                    </Link>{' '}
                                    and save it as <code>const HEAL_API_TOKEN = ...</code>
                                </div>
                            </TimelineElement>
                            <TimelineElement
                                title={
                                    <div className="flex flex-row items-center gap-2">
                                        Use this code snippet
                                        <button
                                            title="Copy node snippet to clipboard"
                                            onClick={() => {
                                                toast({
                                                    title: 'Copied to clipboard',
                                                    duration: 5000,
                                                });
                                                navigator.clipboard.writeText(NODE_SNIPPET);
                                            }}
                                            className="ms-auto px-2 py-1 hover:bg-purple-100 rounded"
                                        >
                                            <Copy size={14} color="rgb(168 85 247)" />
                                        </button>
                                    </div>
                                }
                            >
                                <pre className="px-2 py-3 border rounded-lg bg-gray-100 text-sm">
                                    <code>{NODE_SNIPPET}</code>
                                </pre>
                            </TimelineElement>
                            <TimelineElement
                                title={'(Optional) Pass variables, change the entrypoint, ...'}
                                hideTimeline
                            >
                                <div>
                                    Check out the{' '}
                                    <Link className="underline" target="_blank" to="https://heal.dev/docs/api">
                                        API reference
                                    </Link>
                                </div>
                            </TimelineElement>
                        </div>
                    </TabsContent>
                    <TabsContent value="bash">
                        {' '}
                        <div>
                            <TimelineElement title={'Set your Heal API token'}>
                                <div>
                                    Create a{' '}
                                    <Link to={`/organisation/keys`} target="_blank" className="underline">
                                        Heal API key
                                    </Link>{' '}
                                    and save it somewhere safe. You'll need it in the next step.
                                </div>
                            </TimelineElement>
                            <TimelineElement
                                title={
                                    <div className="flex flex-row items-center gap-2">
                                        Run this code snippet, replacing <code>HEAL_API_TOKEN</code> with your token
                                        <button
                                            title="Copy bash snippet to clipboard"
                                            onClick={() => {
                                                toast({
                                                    title: 'Copied to clipboard',
                                                    duration: 5000,
                                                });
                                                navigator.clipboard.writeText(CURL_SNIPPET);
                                            }}
                                            className="ms-auto px-2 py-1 hover:bg-purple-100 rounded"
                                        >
                                            <Copy size={14} color="rgb(168 85 247)" />
                                        </button>
                                    </div>
                                }
                            >
                                <pre className="px-2 py-3 border rounded-lg bg-gray-100 text-sm">
                                    <code>{CURL_SNIPPET}</code>
                                </pre>
                            </TimelineElement>
                            <TimelineElement
                                title={'(Optional) Pass variables, change the entrypoint, ...'}
                                hideTimeline
                            >
                                <div>
                                    Check out the{' '}
                                    <Link className="underline" target="_blank" to="https://heal.dev/docs/api">
                                        API reference
                                    </Link>
                                </div>
                            </TimelineElement>
                        </div>
                    </TabsContent>
                </Tabs>
            </div>
        </Modal>
    );
};

export default AddToCiModal;
